import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ThumbsUp, ThumbsDown, RotateCcw, Eye } from "lucide-react";
import { thumbnailUrl as optimizedThumb } from "../../utils/cloudinary";
import { useTranslation } from "react-i18next";

function PostPopup({ post, user }) {
  const { t } = useTranslation();

  if (!post) return null;

  return (
    <div className="w-56">
      <Link to={`/profile/${user?.username || post.userId}/post/${post.id}`}>
        <img
          src={optimizedThumb(post.thumbnailUrl || post.imageUrl)}
          alt={post.caption || "Post"}
          className="w-full h-32 object-cover"
        />
      </Link>
      <div className="p-2.5">
        <Link to={`/profile/${user?.username || post.userId}/post/${post.id}`}>
          <p className="text-sm font-medium leading-tight text-th-text">{post.caption}</p>
        </Link>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
          {post.locationName && (
            <span className="text-xs text-th-muted flex items-center gap-1">
              <MapPin size={10} />
              {post.locationName}
            </span>
          )}
          {(post.takenAt || post.createdAt) && (
            <span className="text-[11px] text-th-muted font-mono">
              {new Date(post.takenAt || post.createdAt).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          {post.worthIt !== null && post.worthIt !== undefined && (
            <span className={`whitespace-nowrap inline-flex items-center gap-0.5 text-[11px] font-mono ${
              post.worthIt ? "text-emerald-500" : "text-red-400"
            }`}>
              {post.worthIt ? <ThumbsUp size={9} /> : <ThumbsDown size={9} />}
              {post.worthIt ? t("post.worthIt") : t("post.skip")}
            </span>
          )}
          {post.wouldReturn !== null && post.wouldReturn !== undefined && (
            <span className={`whitespace-nowrap inline-flex items-center gap-0.5 text-[11px] font-mono ${
              post.wouldReturn ? "text-emerald-500" : "text-amber-500"
            }`}>
              <RotateCcw size={9} />
              {post.wouldReturn ? t("post.goBack") : t("post.once")}
            </span>
          )}
        </div>
        {post.viewCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-mono text-th-muted mt-1">
            <Eye size={9} />
            {t("post.view", { count: post.viewCount })}
          </span>
        )}
        <div className="flex items-center justify-between mt-2">
          {user ? (
            <Link
              to={`/profile/${user.username}`}
              className="text-xs text-th-accent font-mono font-medium hover:underline"
            >
              @{user.username}
            </Link>
          ) : (
            <span className="text-xs text-th-muted">{t("post.unknown")}</span>
          )}
          <span className="text-[11px] font-mono uppercase tracking-wider bg-th-surface text-th-text-sec px-2 py-0.5 rounded-md border border-th-border">
            {t(`categories.${post.category}`, post.category)}
          </span>
        </div>
        {user?.travelStyles?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {user.travelStyles.slice(0, 3).map((style) => (
              <span
                key={style}
                className="text-[11px] bg-th-surface text-th-muted px-1.5 py-0.5 rounded-md font-mono border border-th-border"
              >
                {style}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PostPopup;
